/**
 * Search Results Components
 * 
 * Dropdown display for unified Team Comms search results.
 * Shows results from Team Feed (posts/comments) and Messages.
 */

import React from 'react';
import { MessageSquare, FileText, MessageCircle, Loader2, Search } from 'lucide-react';
import { formatTimeAgo } from '../../../utils/timeFormat';
import { useDesign } from '@/contexts/DesignContext';

/**
 * Highlight search term in text
 */
function HighlightedText({ text, searchQuery }) {
  if (!searchQuery || !text) return <span>{text}</span>;
  
  const parts = text.split(new RegExp(`(${searchQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'));
  
  return (
    <span>
      {parts.map((part, i) => 
        part.toLowerCase() === searchQuery.toLowerCase() ? (
          <mark key={i} className="bg-yellow-200 text-yellow-900 rounded px-0.5">
            {part}
          </mark>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </span>
  );
}

/**
 * Individual search result item
 */
function SearchResultItem({ result, searchQuery, onClick }) {
  const { isStudio } = useDesign();
  const getIcon = () => {
    switch (result.type) {
      case 'post':
        return <FileText className="w-4 h-4" />;
      case 'comment':
        return <MessageCircle className="w-4 h-4" />;
      case 'message':
        return <MessageSquare className="w-4 h-4" />;
      default:
        return <FileText className="w-4 h-4" />;
    }
  };

  const getTypeLabel = () => {
    switch (result.type) {
      case 'post':
        return { text: 'Post', className: 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300' };
      case 'comment':
        return { text: 'Comment', className: 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300' };
      case 'message':
        return { text: 'Message', className: 'bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300' };
      default:
        return { text: 'Result', className: 'bg-slate-100 dark:bg-[#3a3d41] text-slate-700 dark:text-slate-200' };
    }
  };

  const typeLabel = getTypeLabel();
  
  const formatTime = (date) => {
    if (!date) return '';
    return formatTimeAgo(date);
  };

  return (
    <button
      onClick={() => onClick(result)}
      className={isStudio
        ? 'st-srrow w-full text-left focus:outline-none'
        : "w-full text-left px-4 py-3 hover:bg-slate-50 dark:hover:bg-white/5 transition-colors border-b border-slate-100 dark:border-white/10 last:border-b-0 focus:outline-none focus:bg-slate-50"}
      data-testid={`search-result-${result.type}-${result.id}`}
    >
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div data-ui="sr-icon" className={`mt-0.5 p-1.5 rounded-md ${
          result.type === 'message' ? 'bg-purple-100 dark:bg-purple-900/50 text-purple-600 dark:text-purple-400' :
          result.type === 'comment' ? 'bg-green-100 dark:bg-green-900/50 text-green-600 dark:text-green-400' :
          'bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400'
        }`}>
          {getIcon()}
        </div>
        
        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Header with type badge and meta info */}
          <div className="flex items-center gap-2 mb-1">
            <span data-ui="sr-kind" className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${typeLabel.className}`}>
              {typeLabel.text}
            </span>
            <span data-ui="sr-meta" className="text-xs text-slate-400 dark:text-slate-500 truncate">
              {result.authorName}
              {result.conversationName && (
                <> • <span className="text-slate-500 dark:text-slate-400">{result.conversationName}</span></>
              )}
            </span>
          </div>
          
          {/* Snippet with highlighted search term */}
          <p className="text-sm text-slate-700 dark:text-slate-200 line-clamp-2">
            <HighlightedText text={result.snippet} searchQuery={searchQuery} />
          </p>
          
          {/* Timestamp */}
          {result.createdAt && (
            <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-1">
              {formatTime(result.createdAt)}
            </p>
          )}
        </div>
      </div>
    </button>
  );
}

/**
 * Main Search Results Dropdown
 */
export function SearchResultsDropdown({
  isSearching,
  isSearchActive,
  feedResults,
  messageResults,
  searchQuery,
  onResultClick,
  onClose
}) {
  const { isStudio } = useDesign();
  const shell = isStudio
    ? 'st-sr absolute top-full left-0 right-0 z-50'
    : 'absolute top-full left-0 right-0 mt-1 bg-card rounded-lg shadow-xl border border-slate-200 dark:border-white/10 z-50';
  const hasResults = feedResults.length > 0 || messageResults.length > 0;

  // Loading state
  if (isSearching) {
    return (
      <div className={shell}>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-blue-500 dark:text-blue-400 mr-2" />
          <span className="text-sm text-slate-500 dark:text-slate-400">Searching...</span>
        </div>
      </div>
    );
  }

  // No results state
  if (isSearchActive && !hasResults) {
    return (
      <div className={shell}>
        <div className="flex flex-col items-center justify-center py-8 px-4">
          {isStudio
            ? <span className="st-fdglyph" aria-hidden="true"><Search /></span>
            : <Search className="w-8 h-8 text-slate-300 mb-2" />}
          <p className="text-sm text-slate-500 dark:text-slate-400 text-center">
            No results found for &quot;<span className="font-medium">{searchQuery}</span>&quot;
          </p>
          <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
            Try different keywords
          </p>
        </div>
      </div>
    );
  }

  // Results
  if (hasResults) {
    return (
      <div className={`${shell} max-h-[70vh] overflow-hidden`}>
        <div className="overflow-y-auto max-h-[70vh]">
          {/* Feed Results Section */}
          {feedResults.length > 0 && (
            <div>
              <div className={isStudio ? 'st-srh sticky top-0' : "sticky top-0 bg-slate-50 dark:bg-[#1e1f22] px-4 py-2 border-b border-slate-200 dark:border-white/10"}>
                <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide flex items-center gap-2">
                  <FileText className="w-3.5 h-3.5" />
                  Team Feed ({feedResults.length})
                </h3>
              </div>
              {feedResults.map((result) => (
                <SearchResultItem
                  key={`feed-${result.type}-${result.id}`}
                  result={result}
                  searchQuery={searchQuery}
                  onClick={onResultClick}
                />
              ))}
            </div>
          )}
          
          {/* Messages Results Section */}
          {messageResults.length > 0 && (
            <div>
              <div className={isStudio ? 'st-srh sticky top-0' : "sticky top-0 bg-slate-50 dark:bg-[#1e1f22] px-4 py-2 border-b border-slate-200 dark:border-white/10"}>
                <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide flex items-center gap-2">
                  <MessageSquare className="w-3.5 h-3.5" />
                  Messages ({messageResults.length})
                </h3>
              </div>
              {messageResults.map((result) => (
                <SearchResultItem
                  key={`message-${result.id}`}
                  result={result}
                  searchQuery={searchQuery}
                  onClick={onResultClick}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Default: show hint
  if (isSearchActive && searchQuery.length < 2) {
    return (
      <div className={shell}>
        <div className="p-4 text-center">
          <Search className="w-6 h-6 text-slate-300 mx-auto mb-2" />
          <p className="text-sm text-slate-500 dark:text-slate-400">Type at least 2 characters to search</p>
        </div>
      </div>
    );
  }

  return null;
}
